import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

// Lightweight i18n. Add a language by adding a dictionary below; missing keys
// fall back to English, then to the key itself. Persisted to localStorage.
//
// The 10 languages here are the top languages by combined native + second-
// language speakers and internet/business use — the standard "top 10 for
// localization" set (English, Chinese, Spanish, Hindi, Arabic, Portuguese,
// Russian, Japanese, French, German).
export type Lang = "en" | "zh" | "es" | "hi" | "ar" | "pt" | "ru" | "ja" | "fr" | "de";

export const LANGS: { id: Lang; label: string }[] = [
  { id: "en", label: "English" },
  { id: "zh", label: "中文" },
  { id: "es", label: "Español" },
  { id: "hi", label: "हिन्दी" },
  { id: "ar", label: "العربية" },
  { id: "pt", label: "Português" },
  { id: "ru", label: "Русский" },
  { id: "ja", label: "日本語" },
  { id: "fr", label: "Français" },
  { id: "de", label: "Deutsch" },
];

// Right-to-left languages — used to set `dir` on <html> alongside `lang`.
export const RTL_LANGS: ReadonlySet<Lang> = new Set(["ar"]);

type Dict = Record<string, string>;

const en: Dict = {
  "nav.overview": "Overview",
  "nav.insights": "Insights",
  "nav.chat": "Chat",
  "nav.agents": "Agents",
  "nav.cortex": "Cortex",
  "nav.scheduler": "Scheduler",
  "nav.issues": "Issues",
  "nav.revisions": "Revisions",
  "nav.calendar": "Calendar",
  "nav.approvals": "Approvals",
  "nav.files": "Files",
  "nav.memory": "Memory",
  "nav.notes": "Notes",
  "nav.knowledge": "Knowledge",
  "nav.tools": "Tools",
  "nav.providers": "Providers",
  "nav.connections": "Connections",
  "nav.vault": "Vault",
  "nav.activity": "Agent to Agent",
  "nav.orgchart": "Org Chart",
  "nav.logs": "Logs",
  "nav.playground": "Playground",
  "nav.terminal": "Terminal",
  "nav.config": "Config",
  "nav.admin": "Admin",
  "nav.chat.thinking": "An agent is thinking…",
  "nav.chat.unread": "{n} new replies",
  "nav.scheduler.active": "{n} active jobs",
  "nav.approvals.pending": "{n} pending approvals",
  "nav.overview.attention": "{n} items need your attention",
  "nav.activity.working": "An agent exchange is in progress…",
  "nav.activity.new": "{n} new agent messages",
  "nav.sidebar.collapse": "Collapse sidebar",
  "nav.sidebar.expand": "Expand sidebar",
  "nav.mobile.menu": "Open navigation",
  "nav.sidebar.resize": "Drag to resize · double-click to reset",
  "nav.reorder.hint": "Drag to reorder navigation",
  "nav.more": "More",
  "nav.more.expand": "Show more",
  "nav.more.collapse": "Show less",
  "nav.more.hidden": "{n} need attention",
  "agent.online": "Online",
  "agent.offline": "Offline",
  "agent.offline.expand": "Show offline agents",
  "agent.offline.collapse": "Hide offline agents",
  "action.search": "Search…",
  "cmdk.noMatches": "No matches",
  "cmdk.group.settings": "Settings",
  "cmdk.loading": "Loading settings…",
  "action.signout": "Sign out",
  "action.changePassword": "Change password",
  "menu.theme": "Theme",
  "menu.language": "Language",
  "menu.admin": "Administration",
  "menu.setup": "Setup",
  "menu.help": "Help and docs",
  "menu.help.docs": "Guide for this page",
  "theme.dark": "Olano Dark",
  "theme.light": "Olano Light",
  "theme.noir": "Cool Black",
  "theme.bw": "Black & White",
  "theme.pastel": "Pastel Green",
  "theme.singapore": "Singapore",
  "theme.midnight": "Midnight",
  "theme.mocha": "Mocha",
  "theme.harbor": "Harbor",
  "theme.sepia": "Sepia",
  "theme.sakura": "Sakura",
  "theme.contrast": "High Contrast",
  "chat.new": "New chat",
  "chat.placeholder": "Message {agent}…",
  "chat.empty": "Start a conversation with {agent}.",
  "chat.waking": "{agent} is waking up…",
  "chat.wakingHint":
    "It will say hello here in a moment. On a brand-new machine that can take up to a minute.",
  "common.you": "You",
  "common.cancel": "Cancel",
  "common.ok": "OK",
  "common.dismiss": "Dismiss",
  // Shared relative-time phrases, rendered by core/relativeTime.ts. The
  // sub-namespace is deliberate: several views predate this and carry their own
  // `time.now` / `time.mins` / `time.minutesAgo` spellings locally, which win
  // over the shared dictionary and must keep doing so.
  "time.ago.now": "just now",
  "time.ago.minute": "1 min ago",
  "time.ago.minutes": "{n} mins ago",
  "time.ago.hour": "1 hour ago",
  "time.ago.hours": "{n} hours ago",
  "time.ago.day": "1 day ago",
  "time.ago.days": "{n} days ago",
  "toast.agentJoined": "{emoji} {name} joined the team",
  "toast.unknownAgent": "Agent “{agent}” is not in your roster.",
};

const zh: Dict = {
  "nav.overview": "概览",
  "nav.insights": "洞察",
  "nav.chat": "对话",
  "nav.agents": "智能体",
  "nav.cortex": "认知",
  "nav.scheduler": "计划任务",
  "nav.issues": "工单",
  "nav.revisions": "版本历史",
  "nav.calendar": "日历",
  "nav.approvals": "审批",
  "nav.files": "文件",
  "nav.memory": "记忆",
  "nav.notes": "笔记",
  "nav.knowledge": "知识库",
  "nav.tools": "工具",
  "nav.providers": "提供商",
  "nav.connections": "连接",
  "nav.vault": "保险库",
  "nav.activity": "智能体互联",
  "nav.orgchart": "组织架构",
  "nav.logs": "日志",
  "nav.playground": "演练场",
  "nav.terminal": "终端",
  "nav.config": "配置",
  "nav.admin": "管理",
  "nav.chat.thinking": "智能体正在思考…",
  "nav.chat.unread": "{n} 条新回复",
  "nav.scheduler.active": "{n} 个活跃任务",
  "nav.approvals.pending": "{n} 个待审批",
  "nav.overview.attention": "{n} 项需要你处理",
  "nav.activity.working": "智能体交互进行中…",
  "nav.activity.new": "{n} 条新的智能体消息",
  "nav.sidebar.collapse": "收起侧边栏",
  "nav.sidebar.expand": "展开侧边栏",
  "nav.mobile.menu": "打开导航",
  "nav.sidebar.resize": "拖动以调整宽度 · 双击重置",
  "nav.reorder.hint": "拖动以重新排序导航",
  "nav.more": "更多",
  "nav.more.expand": "显示更多",
  "nav.more.collapse": "收起",
  "nav.more.hidden": "{n} 项需要关注",
  "agent.online": "在线",
  "agent.offline": "离线",
  "agent.offline.expand": "显示离线智能体",
  "agent.offline.collapse": "隐藏离线智能体",
  "action.search": "搜索…",
  "cmdk.noMatches": "没有匹配项",
  "cmdk.group.settings": "设置",
  "cmdk.loading": "正在加载设置…",
  "action.signout": "退出登录",
  "action.changePassword": "修改密码",
  "menu.theme": "主题",
  "menu.language": "语言",
  "menu.admin": "管理",
  "menu.setup": "设置",
  "menu.help": "帮助与文档",
  "menu.help.docs": "本页面的指南",
  "theme.dark": "Olano 深色",
  "theme.light": "Olano 浅色",
  "theme.noir": "酷黑",
  "theme.bw": "黑白",
  "theme.pastel": "淡绿",
  "theme.singapore": "新加坡",
  "theme.midnight": "午夜蓝",
  "theme.mocha": "摩卡",
  "theme.harbor": "海港",
  "theme.sepia": "复古纸",
  "theme.sakura": "樱花",
  "theme.contrast": "高对比度",
  "chat.new": "新对话",
  "chat.placeholder": "给 {agent} 发消息…",
  "chat.empty": "开始与 {agent} 对话。",
  "chat.waking": "{agent} 正在启动…",
  "chat.wakingHint": "它马上就会在这里向你打招呼。在全新的机器上可能需要一分钟左右。",
  "common.you": "你",
  "common.cancel": "取消",
  "common.ok": "确定",
  "common.dismiss": "关闭",
  "time.ago.now": "刚刚",
  "time.ago.minute": "1 分钟前",
  "time.ago.minutes": "{n} 分钟前",
  "time.ago.hour": "1 小时前",
  "time.ago.hours": "{n} 小时前",
  "time.ago.day": "1 天前",
  "time.ago.days": "{n} 天前",
  "toast.agentJoined": "{emoji} {name} 加入了团队",
  "toast.unknownAgent": "智能体“{agent}”不在你的名单中。",
};

const es: Dict = {
  "nav.overview": "Resumen",
  "nav.insights": "Análisis",
  "nav.chat": "Chat",
  "nav.agents": "Agentes",
  "nav.cortex": "Córtex",
  "nav.scheduler": "Programador",
  "nav.issues": "Incidencias",
  "nav.revisions": "Revisiones",
  "nav.calendar": "Calendario",
  "nav.approvals": "Aprobaciones",
  "nav.files": "Archivos",
  "nav.memory": "Memoria",
  "nav.notes": "Notas",
  "nav.knowledge": "Conocimiento",
  "nav.tools": "Herramientas",
  "nav.providers": "Proveedores",
  "nav.connections": "Conexiones",
  "nav.vault": "Bóveda",
  "nav.activity": "Agente a Agente",
  "nav.orgchart": "Organigrama",
  "nav.logs": "Registros",
  "nav.playground": "Zona de pruebas",
  "nav.terminal": "Terminal",
  "nav.config": "Configuración",
  "nav.admin": "Administración",
  "nav.chat.thinking": "Un agente está pensando…",
  "nav.chat.unread": "{n} respuestas nuevas",
  "nav.scheduler.active": "{n} tareas activas",
  "nav.approvals.pending": "{n} aprobaciones pendientes",
  "nav.overview.attention": "{n} elementos requieren tu atención",
  "nav.activity.working": "Un intercambio entre agentes está en curso…",
  "nav.activity.new": "{n} mensajes nuevos de agentes",
  "nav.sidebar.collapse": "Contraer barra lateral",
  "nav.sidebar.expand": "Expandir barra lateral",
  "nav.mobile.menu": "Abrir navegación",
  "nav.sidebar.resize": "Arrastra para ajustar · doble clic para restablecer",
  "nav.reorder.hint": "Arrastra para reordenar la navegación",
  "nav.more": "Más",
  "nav.more.expand": "Mostrar más",
  "nav.more.collapse": "Mostrar menos",
  "nav.more.hidden": "{n} requieren atención",
  "agent.online": "En línea",
  "agent.offline": "Desconectados",
  "agent.offline.expand": "Mostrar agentes desconectados",
  "agent.offline.collapse": "Ocultar agentes desconectados",
  "action.search": "Buscar…",
  "cmdk.noMatches": "Sin coincidencias",
  "cmdk.group.settings": "Ajustes",
  "cmdk.loading": "Cargando ajustes…",
  "action.signout": "Cerrar sesión",
  "action.changePassword": "Cambiar contraseña",
  "menu.theme": "Tema",
  "menu.language": "Idioma",
  "menu.admin": "Administración",
  "menu.setup": "Configuración inicial",
  "menu.help": "Ayuda y documentación",
  "menu.help.docs": "Guía de esta página",
  "theme.dark": "Olano Oscuro",
  "theme.light": "Olano Claro",
  "theme.noir": "Negro intenso",
  "theme.bw": "Blanco y negro",
  "theme.pastel": "Verde pastel",
  "theme.singapore": "Singapur",
  "theme.midnight": "Medianoche",
  "theme.mocha": "Moca",
  "theme.harbor": "Puerto",
  "theme.sepia": "Sepia",
  "theme.sakura": "Sakura",
  "theme.contrast": "Alto contraste",
  "chat.new": "Nuevo chat",
  "chat.placeholder": "Mensaje para {agent}…",
  "chat.empty": "Empieza una conversación con {agent}.",
  "chat.waking": "{agent} se está despertando…",
  "chat.wakingHint":
    "Saludará aquí en un momento. En una máquina recién creada puede tardar hasta un minuto.",
  "common.you": "Tú",
  "common.cancel": "Cancelar",
  "common.ok": "Aceptar",
  "common.dismiss": "Descartar",
  "time.ago.now": "ahora mismo",
  "time.ago.minute": "hace 1 min",
  "time.ago.minutes": "hace {n} min",
  "time.ago.hour": "hace 1 hora",
  "time.ago.hours": "hace {n} horas",
  "time.ago.day": "hace 1 día",
  "time.ago.days": "hace {n} días",
  "toast.agentJoined": "{emoji} {name} se unió al equipo",
  "toast.unknownAgent": "El agente «{agent}» no está en tu lista.",
};

const hi: Dict = {
  "nav.overview": "अवलोकन",
  "nav.insights": "इनसाइट्स",
  "nav.chat": "चैट",
  "nav.agents": "एजेंट",
  "nav.cortex": "कॉर्टेक्स",
  "nav.scheduler": "शेड्यूलर",
  "nav.issues": "इश्यूज़",
  "nav.revisions": "रिविज़न",
  "nav.calendar": "कैलेंडर",
  "nav.approvals": "अनुमोदन",
  "nav.files": "फ़ाइलें",
  "nav.memory": "मेमोरी",
  "nav.notes": "नोट्स",
  "nav.knowledge": "नॉलेज",
  "nav.tools": "टूल्स",
  "nav.providers": "प्रोवाइडर",
  "nav.connections": "कनेक्शन",
  "nav.vault": "वॉल्ट",
  "nav.activity": "एजेंट-टू-एजेंट",
  "nav.orgchart": "ऑर्ग चार्ट",
  "nav.logs": "लॉग्स",
  "nav.playground": "प्लेग्राउंड",
  "nav.terminal": "टर्मिनल",
  "nav.config": "कॉन्फ़िग",
  "nav.admin": "एडमिन",
  "nav.chat.thinking": "एक एजेंट सोच रहा है…",
  "nav.chat.unread": "{n} नए उत्तर",
  "nav.scheduler.active": "{n} सक्रिय कार्य",
  "nav.approvals.pending": "{n} अनुमोदन लंबित",
  "nav.overview.attention": "{n} आइटम पर आपका ध्यान चाहिए",
  "nav.activity.working": "एजेंट के बीच आदान-प्रदान जारी है…",
  "nav.activity.new": "{n} नए एजेंट संदेश",
  "nav.sidebar.collapse": "साइडबार समेटें",
  "nav.sidebar.expand": "साइडबार खोलें",
  "nav.mobile.menu": "नेविगेशन खोलें",
  "nav.sidebar.resize": "आकार बदलने के लिए खींचें · रीसेट के लिए डबल-क्लिक करें",
  "nav.reorder.hint": "नेविगेशन क्रम बदलने के लिए खींचें",
  "nav.more": "अधिक",
  "nav.more.expand": "और दिखाएं",
  "nav.more.collapse": "कम दिखाएं",
  "nav.more.hidden": "{n} पर ध्यान चाहिए",
  "agent.online": "ऑनलाइन",
  "agent.offline": "ऑफ़लाइन",
  "agent.offline.expand": "ऑफ़लाइन एजेंट दिखाएं",
  "agent.offline.collapse": "ऑफ़लाइन एजेंट छिपाएं",
  "action.search": "खोजें…",
  "cmdk.noMatches": "कोई मेल नहीं",
  "cmdk.group.settings": "सेटिंग्स",
  "cmdk.loading": "सेटिंग्स लोड हो रही हैं…",
  "action.signout": "साइन आउट करें",
  "action.changePassword": "पासवर्ड बदलें",
  "menu.theme": "थीम",
  "menu.language": "भाषा",
  "menu.admin": "प्रशासन",
  "menu.setup": "सेटअप",
  "menu.help": "सहायता और दस्तावेज़",
  "menu.help.docs": "इस पृष्ठ की मार्गदर्शिका",
  "theme.dark": "Olano डार्क",
  "theme.light": "Olano लाइट",
  "theme.noir": "कूल ब्लैक",
  "theme.bw": "ब्लैक एंड व्हाइट",
  "theme.pastel": "पेस्टल ग्रीन",
  "theme.singapore": "सिंगापुर",
  "theme.midnight": "मिडनाइट",
  "theme.mocha": "मोका",
  "theme.harbor": "हार्बर",
  "theme.sepia": "सेपिया",
  "theme.sakura": "साकुरा",
  "theme.contrast": "हाई कंट्रास्ट",
  "chat.new": "नई चैट",
  "chat.placeholder": "{agent} को संदेश भेजें…",
  "chat.empty": "{agent} के साथ बातचीत शुरू करें।",
  "chat.waking": "{agent} जाग रहा है…",
  "chat.wakingHint": "यह कुछ ही पल में यहाँ नमस्ते कहेगा। बिल्कुल नई मशीन पर इसमें एक मिनट तक लग सकता है।",
  "common.you": "आप",
  "common.cancel": "रद्द करें",
  "common.ok": "ठीक है",
  "common.dismiss": "खारिज करें",
  "time.ago.now": "अभी अभी",
  "time.ago.minute": "1 मिनट पहले",
  "time.ago.minutes": "{n} मिनट पहले",
  "time.ago.hour": "1 घंटा पहले",
  "time.ago.hours": "{n} घंटे पहले",
  "time.ago.day": "1 दिन पहले",
  "time.ago.days": "{n} दिन पहले",
  "toast.agentJoined": "{emoji} {name} टीम में शामिल हुआ",
  "toast.unknownAgent": "एजेंट “{agent}” आपकी सूची में नहीं है।",
};

const ar: Dict = {
  "nav.overview": "نظرة عامة",
  "nav.insights": "الرؤى",
  "nav.chat": "الدردشة",
  "nav.agents": "الوكلاء",
  "nav.cortex": "كورتكس",
  "nav.scheduler": "المُجدوِل",
  "nav.issues": "المشكلات",
  "nav.revisions": "المراجعات",
  "nav.calendar": "التقويم",
  "nav.approvals": "الموافقات",
  "nav.files": "الملفات",
  "nav.memory": "الذاكرة",
  "nav.notes": "الملاحظات",
  "nav.knowledge": "المعرفة",
  "nav.tools": "الأدوات",
  "nav.providers": "مزوّدو الخدمة",
  "nav.connections": "الاتصالات",
  "nav.vault": "الخزنة",
  "nav.activity": "وكيل إلى وكيل",
  "nav.orgchart": "الهيكل التنظيمي",
  "nav.logs": "السجلات",
  "nav.playground": "ساحة التجربة",
  "nav.terminal": "الطرفية",
  "nav.config": "الإعدادات",
  "nav.admin": "الإدارة",
  "nav.chat.thinking": "أحد الوكلاء يفكر…",
  "nav.chat.unread": "{n} ردود جديدة",
  "nav.scheduler.active": "{n} مهام نشطة",
  "nav.approvals.pending": "{n} موافقات معلّقة",
  "nav.overview.attention": "{n} عناصر تحتاج انتباهك",
  "nav.activity.working": "تبادل بين الوكلاء قيد التنفيذ…",
  "nav.activity.new": "{n} رسائل وكلاء جديدة",
  "nav.sidebar.collapse": "طي الشريط الجانبي",
  "nav.sidebar.expand": "توسيع الشريط الجانبي",
  "nav.mobile.menu": "فتح التنقّل",
  "nav.sidebar.resize": "اسحب لتغيير الحجم · انقر مرتين للإعادة",
  "nav.reorder.hint": "اسحب لإعادة ترتيب التنقّل",
  "nav.more": "المزيد",
  "nav.more.expand": "عرض المزيد",
  "nav.more.collapse": "عرض أقل",
  "nav.more.hidden": "{n} تحتاج انتباهًا",
  "agent.online": "متصل",
  "agent.offline": "غير متصل",
  "agent.offline.expand": "إظهار الوكلاء غير المتصلين",
  "agent.offline.collapse": "إخفاء الوكلاء غير المتصلين",
  "action.search": "بحث…",
  "cmdk.noMatches": "لا توجد نتائج مطابقة",
  "cmdk.group.settings": "الإعدادات",
  "cmdk.loading": "جارٍ تحميل الإعدادات…",
  "action.signout": "تسجيل الخروج",
  "action.changePassword": "تغيير كلمة المرور",
  "menu.theme": "المظهر",
  "menu.language": "اللغة",
  "menu.admin": "الإدارة",
  "menu.setup": "الإعداد",
  "menu.help": "المساعدة والوثائق",
  "menu.help.docs": "دليل هذه الصفحة",
  "theme.dark": "Olano داكن",
  "theme.light": "Olano فاتح",
  "theme.noir": "أسود بارد",
  "theme.bw": "أبيض وأسود",
  "theme.pastel": "أخضر باستيل",
  "theme.singapore": "سنغافورة",
  "theme.midnight": "منتصف الليل",
  "theme.mocha": "موكا",
  "theme.harbor": "المرفأ",
  "theme.sepia": "سيبيا",
  "theme.sakura": "ساكورا",
  "theme.contrast": "تباين عالٍ",
  "chat.new": "دردشة جديدة",
  "chat.placeholder": "راسل {agent}…",
  "chat.empty": "ابدأ محادثة مع {agent}.",
  "common.you": "أنت",
  "common.cancel": "إلغاء",
  "common.ok": "موافق",
  "common.dismiss": "إغلاق",
  // Numeral + singular noun ("منذ 5 دقيقة") — the form that stays grammatical
  // across Arabic's dual/few/many splits without a plural-rule engine.
  "time.ago.now": "الآن",
  "time.ago.minute": "منذ دقيقة",
  "time.ago.minutes": "منذ {n} دقيقة",
  "time.ago.hour": "منذ ساعة",
  "time.ago.hours": "منذ {n} ساعة",
  "time.ago.day": "منذ يوم",
  "time.ago.days": "منذ {n} يوم",
  "toast.agentJoined": "{emoji} انضم {name} إلى الفريق",
  "toast.unknownAgent": "الوكيل «{agent}» غير موجود في قائمتك.",
};

const pt: Dict = {
  "nav.overview": "Visão geral",
  "nav.insights": "Insights",
  "nav.chat": "Chat",
  "nav.agents": "Agentes",
  "nav.cortex": "Cortex",
  "nav.scheduler": "Agendador",
  "nav.issues": "Problemas",
  "nav.revisions": "Revisões",
  "nav.calendar": "Calendário",
  "nav.approvals": "Aprovações",
  "nav.files": "Arquivos",
  "nav.memory": "Memória",
  "nav.notes": "Notas",
  "nav.knowledge": "Conhecimento",
  "nav.tools": "Ferramentas",
  "nav.providers": "Provedores",
  "nav.connections": "Conexões",
  "nav.vault": "Cofre",
  "nav.activity": "Agente a Agente",
  "nav.orgchart": "Organograma",
  "nav.logs": "Registros",
  "nav.playground": "Playground",
  "nav.terminal": "Terminal",
  "nav.config": "Configuração",
  "nav.admin": "Admin",
  "nav.chat.thinking": "Um agente está pensando…",
  "nav.chat.unread": "{n} novas respostas",
  "nav.scheduler.active": "{n} tarefas ativas",
  "nav.approvals.pending": "{n} aprovações pendentes",
  "nav.overview.attention": "{n} itens precisam da sua atenção",
  "nav.activity.working": "Uma troca entre agentes está em andamento…",
  "nav.activity.new": "{n} novas mensagens de agentes",
  "nav.sidebar.collapse": "Recolher barra lateral",
  "nav.sidebar.expand": "Expandir barra lateral",
  "nav.mobile.menu": "Abrir navegação",
  "nav.sidebar.resize": "Arraste para redimensionar · clique duplo para redefinir",
  "nav.reorder.hint": "Arraste para reordenar a navegação",
  "nav.more": "Mais",
  "nav.more.expand": "Mostrar mais",
  "nav.more.collapse": "Mostrar menos",
  "nav.more.hidden": "{n} precisam de atenção",
  "agent.online": "Online",
  "agent.offline": "Offline",
  "agent.offline.expand": "Mostrar agentes offline",
  "agent.offline.collapse": "Ocultar agentes offline",
  "action.search": "Buscar…",
  "cmdk.noMatches": "Nenhum resultado",
  "cmdk.group.settings": "Configurações",
  "cmdk.loading": "A carregar definições…",
  "action.signout": "Sair",
  "action.changePassword": "Alterar senha",
  "menu.theme": "Tema",
  "menu.language": "Idioma",
  "menu.admin": "Administração",
  "menu.setup": "Configuração",
  "menu.help": "Ajuda e documentação",
  "menu.help.docs": "Guia desta página",
  "theme.dark": "Olano Escuro",
  "theme.light": "Olano Claro",
  "theme.noir": "Preto intenso",
  "theme.bw": "Preto e branco",
  "theme.pastel": "Verde pastel",
  "theme.singapore": "Singapura",
  "theme.midnight": "Meia-noite",
  "theme.mocha": "Moca",
  "theme.harbor": "Porto",
  "theme.sepia": "Sépia",
  "theme.sakura": "Sakura",
  "theme.contrast": "Alto contraste",
  "chat.new": "Novo chat",
  "chat.placeholder": "Mensagem para {agent}…",
  "chat.empty": "Inicie uma conversa com {agent}.",
  "common.you": "Você",
  "common.cancel": "Cancelar",
  "common.ok": "OK",
  "common.dismiss": "Dispensar",
  "time.ago.now": "agora mesmo",
  "time.ago.minute": "há 1 min",
  "time.ago.minutes": "há {n} min",
  "time.ago.hour": "há 1 hora",
  "time.ago.hours": "há {n} horas",
  "time.ago.day": "há 1 dia",
  "time.ago.days": "há {n} dias",
  "toast.agentJoined": "{emoji} {name} entrou na equipe",
  "toast.unknownAgent": "O agente “{agent}” não está na sua lista.",
};

const ru: Dict = {
  "nav.overview": "Обзор",
  "nav.insights": "Аналитика",
  "nav.chat": "Чат",
  "nav.agents": "Агенты",
  "nav.cortex": "Кортекс",
  "nav.scheduler": "Планировщик",
  "nav.issues": "Задачи",
  "nav.revisions": "Ревизии",
  "nav.calendar": "Календарь",
  "nav.approvals": "Согласования",
  "nav.files": "Файлы",
  "nav.memory": "Память",
  "nav.notes": "Заметки",
  "nav.knowledge": "База знаний",
  "nav.tools": "Инструменты",
  "nav.providers": "Провайдеры",
  "nav.connections": "Подключения",
  "nav.vault": "Хранилище",
  "nav.activity": "Агент-агент",
  "nav.orgchart": "Оргструктура",
  "nav.logs": "Логи",
  "nav.playground": "Песочница",
  "nav.terminal": "Терминал",
  "nav.config": "Конфигурация",
  "nav.admin": "Админ",
  "nav.chat.thinking": "Агент думает…",
  "nav.chat.unread": "{n} новых ответов",
  "nav.scheduler.active": "{n} активных задач",
  "nav.approvals.pending": "{n} согласований в ожидании",
  "nav.overview.attention": "{n} элементов требуют внимания",
  "nav.activity.working": "Обмен между агентами выполняется…",
  "nav.activity.new": "{n} новых сообщений от агентов",
  "nav.sidebar.collapse": "Свернуть боковую панель",
  "nav.sidebar.expand": "Развернуть боковую панель",
  "nav.mobile.menu": "Открыть навигацию",
  "nav.sidebar.resize": "Перетащите, чтобы изменить размер · двойной клик — сброс",
  "nav.reorder.hint": "Перетащите, чтобы изменить порядок навигации",
  "nav.more": "Ещё",
  "nav.more.expand": "Показать больше",
  "nav.more.collapse": "Показать меньше",
  "nav.more.hidden": "{n} требуют внимания",
  "agent.online": "В сети",
  "agent.offline": "Не в сети",
  "agent.offline.expand": "Показать офлайн-агентов",
  "agent.offline.collapse": "Скрыть офлайн-агентов",
  "action.search": "Поиск…",
  "cmdk.noMatches": "Совпадений нет",
  "cmdk.group.settings": "Настройки",
  "cmdk.loading": "Загрузка настроек…",
  "action.signout": "Выйти",
  "action.changePassword": "Сменить пароль",
  "menu.theme": "Тема",
  "menu.language": "Язык",
  "menu.admin": "Администрирование",
  "menu.setup": "Настройка",
  "menu.help": "Справка и документация",
  "menu.help.docs": "Руководство по странице",
  "theme.dark": "Olano Тёмная",
  "theme.light": "Olano Светлая",
  "theme.noir": "Холодный чёрный",
  "theme.bw": "Чёрно-белая",
  "theme.pastel": "Пастельно-зелёная",
  "theme.singapore": "Сингапур",
  "theme.midnight": "Полночь",
  "theme.mocha": "Мокко",
  "theme.harbor": "Гавань",
  "theme.sepia": "Сепия",
  "theme.sakura": "Сакура",
  "theme.contrast": "Высокий контраст",
  "chat.new": "Новый чат",
  "chat.placeholder": "Сообщение для {agent}…",
  "chat.empty": "Начните разговор с {agent}.",
  "common.you": "Вы",
  "common.cancel": "Отмена",
  "common.ok": "ОК",
  "common.dismiss": "Закрыть",
  // Abbreviated units ("5 мин назад") — they read naturally at every count and
  // sidestep Russian's one/few/many noun endings, which a {n} slot cannot.
  "time.ago.now": "только что",
  "time.ago.minute": "1 мин назад",
  "time.ago.minutes": "{n} мин назад",
  "time.ago.hour": "1 ч назад",
  "time.ago.hours": "{n} ч назад",
  "time.ago.day": "1 д назад",
  "time.ago.days": "{n} д назад",
  "toast.agentJoined": "{emoji} {name} присоединился к команде",
  "toast.unknownAgent": "Агента «{agent}» нет в вашем списке.",
};

const ja: Dict = {
  "nav.overview": "概要",
  "nav.insights": "インサイト",
  "nav.chat": "チャット",
  "nav.agents": "エージェント",
  "nav.cortex": "コーテックス",
  "nav.scheduler": "スケジューラ",
  "nav.issues": "課題",
  "nav.revisions": "履歴",
  "nav.calendar": "カレンダー",
  "nav.approvals": "承認",
  "nav.files": "ファイル",
  "nav.memory": "メモリ",
  "nav.notes": "メモ",
  "nav.knowledge": "ナレッジ",
  "nav.tools": "ツール",
  "nav.providers": "プロバイダー",
  "nav.connections": "接続",
  "nav.vault": "ボールト",
  "nav.activity": "エージェント間通信",
  "nav.orgchart": "組織図",
  "nav.logs": "ログ",
  "nav.playground": "プレイグラウンド",
  "nav.terminal": "ターミナル",
  "nav.config": "設定",
  "nav.admin": "管理",
  "nav.chat.thinking": "エージェントが考えています…",
  "nav.chat.unread": "{n} 件の新しい返信",
  "nav.scheduler.active": "{n} 件のアクティブなジョブ",
  "nav.approvals.pending": "{n} 件の承認待ち",
  "nav.overview.attention": "{n} 件の対応が必要です",
  "nav.activity.working": "エージェント間のやり取りが進行中です…",
  "nav.activity.new": "{n} 件の新しいエージェントメッセージ",
  "nav.sidebar.collapse": "サイドバーを折りたたむ",
  "nav.sidebar.expand": "サイドバーを展開する",
  "nav.mobile.menu": "ナビゲーションを開く",
  "nav.sidebar.resize": "ドラッグでサイズ変更 · ダブルクリックでリセット",
  "nav.reorder.hint": "ドラッグしてナビゲーションを並べ替え",
  "nav.more": "もっと見る",
  "nav.more.expand": "もっと表示",
  "nav.more.collapse": "表示を減らす",
  "nav.more.hidden": "{n} 件の対応が必要",
  "agent.online": "オンライン",
  "agent.offline": "オフライン",
  "agent.offline.expand": "オフラインのエージェントを表示",
  "agent.offline.collapse": "オフラインのエージェントを非表示",
  "action.search": "検索…",
  "cmdk.noMatches": "一致する項目がありません",
  "cmdk.group.settings": "設定",
  "cmdk.loading": "設定を読み込んでいます…",
  "action.signout": "サインアウト",
  "action.changePassword": "パスワードを変更",
  "menu.theme": "テーマ",
  "menu.language": "言語",
  "menu.admin": "管理",
  "menu.setup": "セットアップ",
  "menu.help": "ヘルプとドキュメント",
  "menu.help.docs": "このページのガイド",
  "theme.dark": "Olano ダーク",
  "theme.light": "Olano ライト",
  "theme.noir": "クールブラック",
  "theme.bw": "モノクロ",
  "theme.pastel": "パステルグリーン",
  "theme.singapore": "シンガポール",
  "theme.midnight": "ミッドナイト",
  "theme.mocha": "モカ",
  "theme.harbor": "ハーバー",
  "theme.sepia": "セピア",
  "theme.sakura": "桜",
  "theme.contrast": "ハイコントラスト",
  "chat.new": "新しいチャット",
  "chat.placeholder": "{agent} にメッセージを送る…",
  "chat.empty": "{agent} との会話を始めましょう。",
  "common.you": "あなた",
  "common.cancel": "キャンセル",
  "common.ok": "OK",
  "common.dismiss": "閉じる",
  "time.ago.now": "たった今",
  "time.ago.minute": "1 分前",
  "time.ago.minutes": "{n} 分前",
  "time.ago.hour": "1 時間前",
  "time.ago.hours": "{n} 時間前",
  "time.ago.day": "1 日前",
  "time.ago.days": "{n} 日前",
  "toast.agentJoined": "{emoji} {name} がチームに参加しました",
  "toast.unknownAgent": "エージェント「{agent}」は一覧にありません。",
};

const fr: Dict = {
  "nav.overview": "Aperçu",
  "nav.insights": "Analyses",
  "nav.chat": "Chat",
  "nav.agents": "Agents",
  "nav.cortex": "Cortex",
  "nav.scheduler": "Planificateur",
  "nav.issues": "Tickets",
  "nav.revisions": "Révisions",
  "nav.calendar": "Calendrier",
  "nav.approvals": "Approbations",
  "nav.files": "Fichiers",
  "nav.memory": "Mémoire",
  "nav.notes": "Notes",
  "nav.knowledge": "Connaissances",
  "nav.tools": "Outils",
  "nav.providers": "Fournisseurs",
  "nav.connections": "Connexions",
  "nav.vault": "Coffre-fort",
  "nav.activity": "Agent à agent",
  "nav.orgchart": "Organigramme",
  "nav.logs": "Journaux",
  "nav.playground": "Bac à sable",
  "nav.terminal": "Terminal",
  "nav.config": "Configuration",
  "nav.admin": "Admin",
  "nav.chat.thinking": "Un agent réfléchit…",
  "nav.chat.unread": "{n} nouvelles réponses",
  "nav.scheduler.active": "{n} tâches actives",
  "nav.approvals.pending": "{n} approbations en attente",
  "nav.overview.attention": "{n} éléments nécessitent votre attention",
  "nav.activity.working": "Un échange entre agents est en cours…",
  "nav.activity.new": "{n} nouveaux messages d'agents",
  "nav.sidebar.collapse": "Réduire la barre latérale",
  "nav.sidebar.expand": "Développer la barre latérale",
  "nav.mobile.menu": "Ouvrir la navigation",
  "nav.sidebar.resize": "Glisser pour redimensionner · double-clic pour réinitialiser",
  "nav.reorder.hint": "Glisser pour réorganiser la navigation",
  "nav.more": "Plus",
  "nav.more.expand": "Afficher plus",
  "nav.more.collapse": "Afficher moins",
  "nav.more.hidden": "{n} nécessitent une attention",
  "agent.online": "En ligne",
  "agent.offline": "Hors ligne",
  "agent.offline.expand": "Afficher les agents hors ligne",
  "agent.offline.collapse": "Masquer les agents hors ligne",
  "action.search": "Rechercher…",
  "cmdk.noMatches": "Aucun résultat",
  "cmdk.group.settings": "Paramètres",
  "cmdk.loading": "Chargement des paramètres…",
  "action.signout": "Se déconnecter",
  "action.changePassword": "Changer le mot de passe",
  "menu.theme": "Thème",
  "menu.language": "Langue",
  "menu.admin": "Administration",
  "menu.setup": "Configuration",
  "menu.help": "Aide et documentation",
  "menu.help.docs": "Guide de cette page",
  "theme.dark": "Olano Sombre",
  "theme.light": "Olano Clair",
  "theme.noir": "Noir intense",
  "theme.bw": "Noir et blanc",
  "theme.pastel": "Vert pastel",
  "theme.singapore": "Singapour",
  "theme.midnight": "Minuit",
  "theme.mocha": "Moka",
  "theme.harbor": "Port",
  "theme.sepia": "Sépia",
  "theme.sakura": "Sakura",
  "theme.contrast": "Contraste élevé",
  "chat.new": "Nouveau chat",
  "chat.placeholder": "Message à {agent}…",
  "chat.empty": "Démarrez une conversation avec {agent}.",
  "common.you": "Vous",
  "common.cancel": "Annuler",
  "common.ok": "OK",
  "common.dismiss": "Ignorer",
  "time.ago.now": "à l'instant",
  "time.ago.minute": "il y a 1 min",
  "time.ago.minutes": "il y a {n} min",
  "time.ago.hour": "il y a 1 heure",
  "time.ago.hours": "il y a {n} heures",
  "time.ago.day": "il y a 1 jour",
  "time.ago.days": "il y a {n} jours",
  "toast.agentJoined": "{emoji} {name} a rejoint l'équipe",
  "toast.unknownAgent": "L’agent « {agent} » n’est pas dans votre liste.",
};

const de: Dict = {
  "nav.overview": "Übersicht",
  "nav.insights": "Insights",
  "nav.chat": "Chat",
  "nav.agents": "Agenten",
  "nav.cortex": "Cortex",
  "nav.scheduler": "Planer",
  "nav.issues": "Vorgänge",
  "nav.revisions": "Revisionen",
  "nav.calendar": "Kalender",
  "nav.approvals": "Genehmigungen",
  "nav.files": "Dateien",
  "nav.memory": "Speicher",
  "nav.notes": "Notizen",
  "nav.knowledge": "Wissen",
  "nav.tools": "Werkzeuge",
  "nav.providers": "Anbieter",
  "nav.connections": "Verbindungen",
  "nav.vault": "Tresor",
  "nav.activity": "Agent-zu-Agent",
  "nav.orgchart": "Organigramm",
  "nav.logs": "Protokolle",
  "nav.playground": "Playground",
  "nav.terminal": "Terminal",
  "nav.config": "Konfiguration",
  "nav.admin": "Admin",
  "nav.chat.thinking": "Ein Agent denkt nach…",
  "nav.chat.unread": "{n} neue Antworten",
  "nav.scheduler.active": "{n} aktive Aufgaben",
  "nav.approvals.pending": "{n} ausstehende Genehmigungen",
  "nav.overview.attention": "{n} Elemente erfordern Ihre Aufmerksamkeit",
  "nav.activity.working": "Ein Austausch zwischen Agenten läuft…",
  "nav.activity.new": "{n} neue Agentennachrichten",
  "nav.sidebar.collapse": "Seitenleiste einklappen",
  "nav.sidebar.expand": "Seitenleiste ausklappen",
  "nav.mobile.menu": "Navigation öffnen",
  "nav.sidebar.resize": "Ziehen zum Anpassen der Größe · Doppelklick zum Zurücksetzen",
  "nav.reorder.hint": "Ziehen, um die Navigation neu anzuordnen",
  "nav.more": "Mehr",
  "nav.more.expand": "Mehr anzeigen",
  "nav.more.collapse": "Weniger anzeigen",
  "nav.more.hidden": "{n} erfordern Aufmerksamkeit",
  "agent.online": "Online",
  "agent.offline": "Offline",
  "agent.offline.expand": "Offline-Agenten anzeigen",
  "agent.offline.collapse": "Offline-Agenten ausblenden",
  "action.search": "Suchen…",
  "cmdk.noMatches": "Keine Treffer",
  "cmdk.group.settings": "Einstellungen",
  "cmdk.loading": "Einstellungen werden geladen…",
  "action.signout": "Abmelden",
  "action.changePassword": "Passwort ändern",
  "menu.theme": "Design",
  "menu.language": "Sprache",
  "menu.admin": "Verwaltung",
  "menu.setup": "Einrichtung",
  "menu.help": "Hilfe und Dokumentation",
  "menu.help.docs": "Hilfe zu dieser Seite",
  "theme.dark": "Olano Dunkel",
  "theme.light": "Olano Hell",
  "theme.noir": "Kühles Schwarz",
  "theme.bw": "Schwarz-Weiß",
  "theme.pastel": "Pastellgrün",
  "theme.singapore": "Singapur",
  "theme.midnight": "Mitternacht",
  "theme.mocha": "Mokka",
  "theme.harbor": "Hafen",
  "theme.sepia": "Sepia",
  "theme.sakura": "Sakura",
  "theme.contrast": "Hoher Kontrast",
  "chat.new": "Neuer Chat",
  "chat.placeholder": "Nachricht an {agent}…",
  "chat.empty": "Beginnen Sie ein Gespräch mit {agent}.",
  "common.you": "Sie",
  "common.cancel": "Abbrechen",
  "common.ok": "OK",
  "common.dismiss": "Schließen",
  "time.ago.now": "gerade eben",
  "time.ago.minute": "vor 1 Min.",
  "time.ago.minutes": "vor {n} Min.",
  "time.ago.hour": "vor 1 Stunde",
  "time.ago.hours": "vor {n} Stunden",
  "time.ago.day": "vor 1 Tag",
  "time.ago.days": "vor {n} Tagen",
  "toast.agentJoined": "{emoji} {name} ist dem Team beigetreten",
  "toast.unknownAgent": "Agent „{agent}“ ist nicht in Ihrer Liste.",
};

const DICTS: Record<Lang, Dict> = { en, zh, es, hi, ar, pt, ru, ja, fr, de };
const KEY = "lc-lang";

function initialLang(): Lang {
  const saved = localStorage.getItem(KEY);
  return (Object.prototype.hasOwnProperty.call(DICTS, saved ?? "") ? saved : "en") as Lang;
}

interface I18n {
  lang: Lang;
  setLang(lang: Lang): void;
  t(key: string, vars?: Record<string, string>): string;
}

const Ctx = createContext<I18n | null>(null);

/**
 * Substitute `{name}` placeholders.
 *
 * `replaceAll`, not `replace`: a message is free to name the same value twice
 * ("{asker} asked {name}, and it answers inside {asker}'s reply"), and the
 * single-shot version left every repeat on screen as a literal `{asker}` —
 * which is what `addbot.whoHelp` and `ob.acct.doneNotSignedIn` were already
 * printing. The function form of the replacement is deliberate too: a string
 * replacement treats `$&` and `$1` in the VALUE as references, so a bot named
 * with a `$` would mangle itself.
 */
function fill(text: string, vars: Record<string, string>): string {
  let out = text;
  for (const [k, v] of Object.entries(vars)) out = out.replaceAll(`{${k}}`, () => v);
  return out;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang());

  // Apply lang/dir on mount too, so a persisted non-English (or RTL) choice
  // is reflected on <html> before the user touches the language menu again.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.has(lang) ? "rtl" : "ltr";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = useCallback((next: Lang) => {
    localStorage.setItem(KEY, next);
    document.documentElement.lang = next;
    document.documentElement.dir = RTL_LANGS.has(next) ? "rtl" : "ltr";
    setLangState(next);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string>) => {
      let s = DICTS[lang][key] ?? en[key] ?? key;
      if (vars) s = fill(s, vars);
      return s;
    },
    [lang],
  );

  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider>");
  return ctx;
}

// Per-view translations: define a module-level dict and call useT(dict) to get a
// `t` that prefers the view's strings, then falls back to the shared dictionary,
// then the key. Keeps i18n local to each view instead of one giant central file.
export type LocalDict = Partial<Record<Lang, Record<string, string>>>;

/**
 * The local-dict half of useT's lookup, without the hook: the requested
 * language, then English, then undefined so the caller can fall back to the
 * central dictionary. Exported because core/settingsIndex resolves labels for
 * entries whose owning view is in another chunk and has no React context to
 * read — one fallback rule, used from both places.
 */
export function resolveLocal(local: LocalDict | undefined, lang: Lang, key: string): string | undefined {
  return local?.[lang]?.[key] ?? local?.en?.[key];
}

export function useT(local: LocalDict) {
  const { lang, t } = useI18n();
  return useCallback(
    (key: string, vars?: Record<string, string>) => {
      const hit = resolveLocal(local, lang, key);
      if (hit === undefined) return t(key, vars);
      let s = hit;
      if (vars) s = fill(s, vars);
      return s;
    },
    [lang, t, local],
  );
}
